import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Wind, Compass, Users, Sunset } from 'lucide-react';

const faqs = [
  {
    q: "How close is Rudragarh to Sam Sand Dunes?",
    a: "Rudragarh is located near Sam Sand Dunes — the most famous dune stretch in Rajasthan. The resort sits close enough that guests can watch sunset colours change over the dunes from the property. The iconic Sam dune belt is reachable within a short drive, but Rudragarh's own private dunes mean you rarely need to go elsewhere."
  },
  {
    q: "What dune experiences are available at Rudragarh?",
    a: "Guests have access to ATV rides directly on the property's private dunes, camel safaris around the desert, horse riding, sunset and sunrise dune walks, and guided stargazing sessions after dark. The open-air theatre and cultural programs take place against a dune backdrop. Everything is on-site — no separate bookings or extra charges for most activities."
  },
  {
    q: "Is Rudragarh near Sam Sand Dunes suitable for families?",
    a: "Yes — Rudragarh is particularly well-suited for families. There's a dedicated kids' play zone, shallow pool access, family-friendly room options like The Heritage Huts and The Grand Quad (both sleeping 4 guests), and supervised adventure activities including archery and football. Cultural evenings are family-friendly, and the dining hall caters to all ages."
  }
];

const highlights = [
  { icon: <Wind size={28} />, title: "Private Dunes On-Site", desc: "No day-trip needed. Rudragarh's own sand dunes are steps from your door — for ATVs, horses, and golden-hour walks." },
  { icon: <Sunset size={28} />, title: "Dune Sunsets & Stargazing", desc: "Watch the Thar turn amber at dusk. After dark, the sky above Sam is one of the most star-filled in India." },
  { icon: <Compass size={28} />, title: "ATV Rides & Camel Safari", desc: "Tear across the dunes on an ATV or sway atop a camel at dawn — both available directly on the property." },
  { icon: <Users size={28} />, title: "Family-Friendly Stay", desc: "Kids' play zone, family suites, and activities for all ages — the desert is for everyone at Rudragarh." }
];

const rooms = [
  { slug: "heritage-huts", name: "The Heritage Huts", price: 15000, guests: 4, category: "unique", desc: "Two-level eco-chic bamboo huts with an open kitchen and private porch — ideal for families or groups." },
  { slug: "grand-quad", name: "The Grand Quad", price: 7000, guests: 4, category: "modern", desc: "The resort's grandest room with a king bed — steps from the bar, pool and garden." },
  { slug: "catacomb-suite", name: "The Catacomb Suite", price: 10000, guests: 4, category: "unique", desc: "Subterranean carved-cave cottages with natural climate control, two double beds, and a private attached bathroom." }
];

const NearSamSandDunes = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Resort Near Sam Sand Dunes, Jaisalmer | Rudragarh</title>
        <meta name="description" content="Rudragarh Resort is located near the Sam Sand Dunes, Jaisalmer. Cave stays, ATV rides on the dunes, camel safaris, and stargazing — the desert, right at your doorstep." />
        <meta property="og:title" content="Resort Near Sam Sand Dunes, Jaisalmer | Rudragarh" />
        <meta property="og:description" content="Stay steps from Sam Sand Dunes at Rudragarh — cave rooms, ATV rides, camel safaris, private dunes, and stargazing in Jaisalmer." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/jaisalmer/resort-near-sam-sand-dunes" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pond.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Resort Near Sam Sand Dunes | Rudragarh, Jaisalmer" />
        <meta name="twitter:description" content="Rudragarh Resort — cave stays, ATV rides, camel safaris, and private dunes near Sam Sand Dunes, Jaisalmer." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pond.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/jaisalmer/resort-near-sam-sand-dunes" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pond.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/45 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Near Sam Sand Dunes</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Steps from the <span className="text-gold-500">Sam Dunes</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              Wake up to golden dunes, ride an ATV before breakfast, watch the Milky Way after dinner. Rudragarh puts Sam Sand Dunes right at your doorstep.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="overline">The Location</span>
              <h2 className="text-3xl md:text-4xl font-heading font-medium">
                The Sam Sand Dunes, <span className="text-gold-500">right outside your room</span>
              </h2>
              <p className="text-foreground/70 leading-relaxed">
                Sam Sand Dunes are the crown jewel of the Thar Desert — vast, pristine, and eerily beautiful. Most travellers visit them on a day trip, spend an hour for sunset, and head back to Jaisalmer city. At Rudragarh, you stay here. The dunes become your morning view, your evening backdrop, and your playground.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                The property has its own private dunes — so even in peak season, you're never jostling for space with tour groups. ATV rides, camel safaris, and horse rides all operate directly on Rudragarh's land. When the tour buses leave at dusk, the desert is yours.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-void-subtle border border-gold-500/10 p-8 space-y-6"
            >
              <h3 className="font-heading text-2xl">What's on the dunes</h3>
              {[
                { label: "ATV Rides", detail: "On Rudragarh's own dune stretch — no separate booking needed" },
                { label: "Camel Safari", detail: "Sunrise and sunset routes through the Thar" },
                { label: "Horse Riding", detail: "Guided desert rides across the property" },
                { label: "Stargazing", detail: "Sam's skies are some of the darkest in Rajasthan" },
                { label: "Dune Trekking", detail: "Guided walks on the private dune belt" },
                { label: "Cultural Evenings", detail: "Folk music and dance by the open-air theatre" }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 pb-4 border-b border-gold-500/10 last:border-0 last:pb-0">
                  <div className="w-1.5 h-1.5 bg-gold-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <span className="font-heading text-base">{item.label}</span>
                    <p className="text-foreground/50 text-sm mt-1">{item.detail}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">The Experience</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Why Stay <span className="text-gold-500">Near Sam</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Accommodations</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Sleep Beside the <span className="text-gold-500">Dunes</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              About <span className="text-gold-500">Sam Sand Dunes</span> Stays
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-hut-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Book Your Stay</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            The Dunes Are <span className="text-gold-500">Waiting</span>
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Reserve your place near Sam Sand Dunes and experience the Thar Desert at its most immersive — from inside Rudragarh.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book Your Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default NearSamSandDunes;
